import { newsItems } from "@/mocks/news";
import { categories } from "@/mocks/categories";
import type { CategoryKey, CategoryMeta, NewsItem } from "@/types/news";

export const allNews: NewsItem[] = newsItems as NewsItem[];

export function getCategories(): CategoryMeta[] {
  return categories as CategoryMeta[];
}

export function getCategoryBySlug(slug: string): CategoryMeta | undefined {
  return (categories as CategoryMeta[]).find((c) => c.slug === slug);
}

export function getCategoryByKey(key: CategoryKey): CategoryMeta | undefined {
  return (categories as CategoryMeta[]).find((c) => c.key === key);
}

export function getNewsById(id: string): NewsItem | undefined {
  return allNews.find((n) => n.id === id);
}

export function getNewsByCategory(key: CategoryKey): NewsItem[] {
  return allNews.filter((n) => n.category === key);
}

export function getNewsByTag(tag: string): NewsItem[] {
  return allNews.filter((n) => n.tags.includes(tag) || n.title.includes(tag));
}

export function searchNews(keyword: string): NewsItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return allNews.filter(
    (n) =>
      n.title.toLowerCase().includes(kw) ||
      n.excerpt.toLowerCase().includes(kw) ||
      n.tags.some((t) => t.toLowerCase().includes(kw)) ||
      n.content.some((p) => p.toLowerCase().includes(kw))
  );
}

export function getRelatedNews(item: NewsItem, count = 5): NewsItem[] {
  const sameCategory = allNews.filter(
    (n) => n.category === item.category && n.id !== item.id
  );
  const others = allNews.filter(
    (n) => n.category !== item.category && n.id !== item.id
  );
  return [...sameCategory, ...others].slice(0, count);
}

export type SortKey = "latest" | "hot";
export type TimeRange = "all" | "24h" | "7d" | "30d";

export function parseTime(value: string): number {
  return new Date(value.replace(/-/g, "/")).getTime();
}

export function filterAndSort(
  items: NewsItem[],
  options: { timeRange?: TimeRange; sortBy?: SortKey } = {}
): NewsItem[] {
  const { timeRange = "all", sortBy = "latest" } = options;
  const now = Date.now();
  const rangeMs: Record<TimeRange, number> = {
    all: Number.POSITIVE_INFINITY,
    "24h": 24 * 60 * 60 * 1000,
    "7d": 7 * 24 * 60 * 60 * 1000,
    "30d": 30 * 24 * 60 * 60 * 1000,
  };
  const filtered = items.filter((n) => {
    if (timeRange === "all") return true;
    return now - parseTime(n.publishedAt) <= rangeMs[timeRange];
  });
  return [...filtered].sort((a, b) => {
    if (sortBy === "hot") return b.views - a.views;
    return parseTime(b.publishedAt) - parseTime(a.publishedAt);
  });
}

export function paginate<T>(items: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return String(views);
}

export function formatDateTime(value: string): string {
  return value;
}

export function relativeTime(value: string): string {
  const diff = Date.now() - parseTime(value);
  const min = Math.floor(diff / 60000);
  const hour = Math.floor(diff / 3600000);
  const day = Math.floor(diff / 86400000);
  if (min < 1) return "刚刚";
  if (min < 60) return `${min}分钟前`;
  if (hour < 24) return `${hour}小时前`;
  if (day < 30) return `${day}天前`;
  return value.slice(0, 10);
}

export function getTodayHeadlines(count = 5): NewsItem[] {
  return filterAndSort(allNews, { sortBy: "hot" }).slice(0, count);
}